import React, { useState } from 'react';

/**
 * What must never leave.
 *
 * <h3>Why these are listed and not written here</h3>
 *
 * Every option is one of the tenant's own data classification rules. A rule
 * authored on this screen would be a rule the Data Protection surface does not
 * know about, and "the boundary" would mean two different things in two places.
 * So this lists what exists and switches it on.
 *
 * <h3>Why what is already enforced starts on</h3>
 *
 * Unticking a live boundary here would read as turning it off, and this step
 * does not turn anything off. It adds.
 */
export default function BoundariesAsk({ categories = [], onAnswer, busy = false }) {
  const [on, setOn] = useState(() => new Set(
    categories.filter((c) => c.on).map((c) => c.category),
  ));

  const toggle = (c) => setOn((prev) => {
    const next = new Set(prev);
    if (next.has(c.category)) next.delete(c.category); else next.add(c.category);
    return next;
  });

  if (!categories.length) {
    return (
      <p style={NOTE}>
        No boundary rules are configured for your tenant yet, so there is nothing to switch on
        here. The agent is protected either way — rules can be added from Data Protection and
        they apply to it from the moment they are enabled.
      </p>
    );
  }

  /* In the order they were listed, so the answer reads the same way twice. */
  const chosen = categories.filter((c) => on.has(c.category));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {categories.map((c) => {
          const picked = on.has(c.category);
          return (
            <button key={c.category} type="button" aria-pressed={picked}
                    onClick={() => toggle(c)}
                    style={{
                      cursor: 'pointer', padding: '8px 13px',
                      borderRadius: 'var(--p7-radius-pill, 999px)',
                      border: `1px solid ${picked ? 'var(--p7-accent)' : 'var(--p7-line-strong)'}`,
                      background: picked ? 'var(--p7-accent-bg)' : 'var(--p7-surface)',
                      font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                      color: 'var(--p7-ink)',
                    }}>
              {c.label}
              {c.blocking ? null : (
                <span style={{ marginLeft: '6px', font: 'var(--p7-text-caption)',
                               fontFamily: 'var(--p7-font-sans)',
                               color: 'var(--p7-ink-subtle)' }}>
                  redacts
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                onClick={() => onAnswer({
                  ruleIds: chosen.flatMap((c) => c.ruleIds || []),
                  labels: chosen.map((c) => c.label),
                })}>
          Enforce these
        </button>
      </div>
    </div>
  );
}

const NOTE = {
  margin: 0, font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
  color: 'var(--p7-ink-subtle)', lineHeight: 1.5,
};
